import { type ChosenPayee, PartyPayeeLookup } from 'components/PartyPayeeLookup'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { createLoanPayable, updateLoanPayable } from 'services'
import { Input2, Modal, Select2 } from 'stories/components'
import { removeComma, thousandSeperatorWithPrefix } from 'utils'

import { initialLedgerBucket } from './initialLedgerBucket'

/** Someone on the loan who can be paid. */
export interface Payee {
  payeeType: string
  payeeRefId: number | null
  name: string
  address: string
  achReady: boolean
  achBlockedReason: string
}

/** A payment the loan's own records already justify, offered rather than rekeyed. */
export interface Suggestion {
  sourceType: string
  sourceId: number
  payeeType: string
  payeeName: string
  amount: number
  memo: string
  ledgerCategory: string
  /** Which trust bucket the server proposes booking it in; see `LEDGER_BUCKETS`. */
  ledgerBucket?: string
  label: string
}

/**
 * A queued payment being corrected rather than raised. Only the fields this dialog owns; the payee
 * is matched back to the loan's list by id where it can be, so editing the amount does not quietly
 * turn a linked vendor back into free text.
 */
export interface EditablePayable {
  id: number
  payeeType: string
  payeeRefId?: number | null
  payeeName: string
  payeeAddress?: string
  amount: number
  memo: string
  ledgerCategory: string
  ledgerBucket?: string
}

/**
 * Categories a payout can be booked under. This becomes the trust ledger subType on the
 * disbursement, so it is the loan's ledger that has to read sensibly, not just this screen.
 *
 * 'Refund' and 'Construction Draw' must stay spelled exactly as they are: the server's suggestions
 * emit those two strings, and a prefill landing on a value the list does not contain would appear
 * as an empty category.
 */
export const LEDGER_CATEGORIES: Record<string, string> = {
  'Construction Draw': 'Construction Draw',
  Refund: 'Refund',
  Inspection: 'Inspection',
  Appraisal: 'Appraisal',
  'Property Taxes': 'Property Taxes',
  Insurance: 'Insurance',
  Utilities: 'Utilities',
  'Repairs & Maintenance': 'Repairs & Maintenance',
  'Property Management': 'Property Management',
  Legal: 'Legal',
  Payoff: 'Payoff',
  Other: 'Other',
}

/**
 * Which trust bucket paying this posts the disbursement to, or that it posts none.
 *
 * Separate from the category above, which is the subType WITHIN the bucket: a Legal payment booked
 * on Impounds is still Legal. Every payment used to post to the Construction Reserve whatever its
 * category - that table IS the Construction Reserve tab, and its rows are mirrored into the Draw
 * disbursement Ledger - so a vendor invoice read as a construction draw and there was no way to pay
 * somebody without touching the ledger at all.
 *
 * Keys must match `payable-ledger-bucket.ts` on the server, which refuses anything else. The Draw
 * disbursement Ledger is deliberately absent: it is derived from the Construction Reserve rows, so
 * a draw payout belongs on `constructionReserve` and that tab shows it by itself.
 */
export const LEDGER_BUCKETS: Record<string, string> = {
  constructionReserve: 'Construction Reserve',
  origination: 'Origination Trust',
  escrow: 'Impounds Ledger',
  escrowRepairHoldback: 'Escrow Repair Holdback Ledger',
  interest: 'Interest Reserve Ledger',
  suspense: 'Suspense',
  none: 'Do not post to the trust ledger',
}

/** Raise a payment to someone on the loan. */
export const AddPayableModal = ({
  payees,
  prefill,
  editing,
  loanNumber,
  constructionBucket,
  onClose,
  onDone,
}: {
  payees: Payee[]
  prefill: Partial<Suggestion> | null
  /** A queued payment to correct in place, instead of raising a new one. */
  editing?: EditablePayable | null
  /**
   * Which bucket holds this loan's construction money, as the server resolved it, so a draw opens
   * on the account it comes out of. The Construction Reserve on an originated loan; the Escrow
   * Repair Holdback on one converted from a prior servicer, where the reserve was never funded.
   */
  constructionBucket?: string
  /**
   * Raise the payment against this loan rather than the one in context (the invoice list).
   * `0` raises it against no loan at all: a company invoice that carries no loan number.
   */
  loanNumber?: number | string
  onClose: () => void
  onDone: () => void
}) => {
  // A suggestion prefill either names a party already on the loan, or a one-off payee.
  const initialChosen = useMemo<ChosenPayee | null>(() => {
    // Editing starts from what the payment already stores, matched back to the loan's own list by
    // id so the picker shows the linked party rather than the same name as free text.
    if (editing) {
      const onList = payees.find(
        (p) => p.payeeType === editing.payeeType && (p.payeeRefId ?? null) === (editing.payeeRefId ?? null),
      )
      if (onList)
        return {
          payeeType: onList.payeeType,
          payeeRefId: onList.payeeRefId,
          name: onList.name,
          address: onList.address,
          achReady: onList.achReady,
          achBlockedReason: onList.achBlockedReason,
        }
      return {
        payeeType: editing.payeeType,
        payeeRefId: editing.payeeRefId ?? null,
        name: editing.payeeName,
        address: editing.payeeAddress || '',
      }
    }
    if (!prefill?.payeeName) return null
    const wanted = prefill.payeeName.trim().toLowerCase()
    // A prefill raised from a record that only stores a name - an invoice payee - carries no payee
    // type, so matching on type as well would never hit and the payment would drop to free text
    // even when that exact party is on the list. Fall back to the name alone, and only when it
    // picks out one party: two payees sharing a name are not the same account to send money to.
    const named = payees.filter((p) => p.name.trim().toLowerCase() === wanted)
    const match = prefill.payeeType
      ? payees.find((p) => p.payeeType === prefill.payeeType && p.name === prefill.payeeName)
      : named.length === 1
        ? named[0]
        : undefined
    if (match)
      return {
        payeeType: match.payeeType,
        payeeRefId: match.payeeRefId,
        name: match.name,
        address: match.address,
        achReady: match.achReady,
        achBlockedReason: match.achBlockedReason,
      }
    return { payeeType: 'Other', payeeRefId: null, name: prefill.payeeName, address: '' }
  }, [editing, prefill, payees])

  const [chosen, setChosen] = useState<ChosenPayee | null>(initialChosen)
  const [amount, setAmount] = useState(String(editing?.amount || prefill?.amount || ''))
  const [memo, setMemo] = useState(editing?.memo || prefill?.memo || '')
  const initialCategory = editing?.ledgerCategory || prefill?.ledgerCategory || ''
  const [category, setCategory] = useState(initialCategory)
  const [bucket, setBucket] = useState(
    initialLedgerBucket(editing?.ledgerBucket, prefill?.ledgerBucket, initialCategory, constructionBucket),
  )
  // Whether the operator has set the bucket themselves. Until they do, it follows the category:
  // picking Construction Draw moves it onto the loan's construction bucket, anything else back to
  // posting nothing. Once they have chosen, the category stops moving it, or picking a category
  // would silently undo the bucket they just set.
  const [bucketChosen, setBucketChosen] = useState(false)
  const [saving, setSaving] = useState(false)

  // A payment with no loan behind it books on the trust account itself, so nothing here should
  // promise a loan ledger line that will never appear.
  const onNoLoan = loanNumber !== undefined && Number(loanNumber) === 0

  // A suggestion may arrive with a category this list does not carry; keep it rather than
  // silently blanking what the server proposed.
  const categoryOptions = useMemo(() => {
    if (!category || LEDGER_CATEGORIES[category]) return LEDGER_CATEGORIES
    return { ...LEDGER_CATEGORIES, [category]: category }
  }, [category])

  const name = chosen?.name || ''
  const numericAmount = Number(removeComma(String(amount))) || 0
  // The category becomes the subType on the trust-ledger disbursement, so a payment queued without
  // one pays out uncategorised and nobody goes back to fix it. Longhorn asked for it to be
  // required; the server refuses it too.
  const disabled = saving || !name.trim() || !(numericAmount > 0) || !category.trim()

  const onSubmit = async () => {
    setSaving(true)
    const failed = editing ? 'Could not update this payment.' : 'Could not raise this payment.'
    try {
      const payee = {
        payeeType: chosen?.payeeType || 'Other',
        payeeRefId: chosen?.payeeRefId ?? null,
        payeeName: name.trim(),
        payeeAddress: chosen?.address || '',
        amount: numericAmount,
        memo,
        ledgerCategory: category,
        ledgerBucket: bucket,
      }
      // An edit never restates where the payment came from: the server keeps the original source,
      // so a draw stays a draw and the suggestion it came from is not offered again.
      const res: any = editing
        ? await updateLoanPayable(editing.id, payee, loanNumber)
        : await createLoanPayable(
            { ...payee, sourceType: prefill?.sourceType || 'manual', sourceId: prefill?.sourceId },
            loanNumber,
          )
      if (!res?.success) {
        // Declining the closed-loan warning is not a failure: leave the form as it was keyed.
        if (!res?.cancelled) toast(res?.message || failed, { type: 'error' })
        setSaving(false)
        return
      }
      toast(
        editing
          ? `Payment to ${name.trim()} updated to ${thousandSeperatorWithPrefix(numericAmount)}.`
          : `${thousandSeperatorWithPrefix(numericAmount)} to ${name.trim()} added to the check queue.`,
        { type: 'success' },
      )
      onDone()
    } catch {
      toast(failed, { type: 'error' })
      setSaving(false)
    }
  }

  return (
    <Modal
      title={editing ? 'Edit Party Payment' : 'Pay a Party'}
      isOpen
      onClose={onClose}
      onOk={onSubmit}
      loading={saving}
      disabled={disabled}
      titleOkay={editing ? 'Save Changes' : 'Add to Check Queue'}
    >
      <div className="w-[34rem] max-w-full flex flex-col gap-3">
        <PartyPayeeLookup parties={payees} value={chosen} onSelect={setChosen} />
        {chosen && chosen.achReady === false && (
          <p className="text-xs text-amber-700 -mt-1">
            {chosen.achBlockedReason} — this one can still be paid by check.
          </p>
        )}
        <Input2 title="Amount" type="thousandSep" prefix="$" value={amount} onChange={(v: string) => setAmount(v)} />
        <Input2
          title="Reason"
          value={memo}
          onChange={(v: string) => setMemo(v)}
          placeholder="e.g. Draw 3 - framing, refund of credit balance"
        />
        <Select2
          id="payable-category"
          title="Trust ledger category"
          options={categoryOptions}
          value={category}
          onChange={(v: any) => {
            setCategory(v)
            if (!bucketChosen)
              setBucket(initialLedgerBucket(editing?.ledgerBucket, prefill?.ledgerBucket, v, constructionBucket))
          }}
          hasDefaultOption
          required
        />
        {!onNoLoan && (
          <>
            <Select2
              id="payable-ledger-bucket"
              title="Post to trust ledger"
              options={LEDGER_BUCKETS}
              value={bucket}
              onChange={(v: any) => {
                setBucketChosen(true)
                setBucket(v)
              }}
            />
            <p className="text-xs text-gray-500 -mt-2">
              {bucket === 'none'
                ? 'Nothing is posted to any trust ledger. The payment is recorded against the payee only, so use this when the money is already accounted for somewhere else.'
                : `Paying this posts a disbursement to ${LEDGER_BUCKETS[bucket] || LEDGER_BUCKETS.constructionReserve}.`}
            </p>
          </>
        )}
        <p className="text-xs text-gray-500">
          {editing
            ? 'This payment is still in the servicing check queue. It can be changed until the check is issued.'
            : bucket === 'none'
              ? 'This is added to the servicing check queue and paid from there like any other payment.'
              : 'This is added to the servicing check queue. Nothing posts to the trust ledger until it is actually paid.'}
        </p>
        {onNoLoan && (
          <p className="text-xs text-amber-700 -mt-2">
            This invoice has no loan number, so the payment is not tied to a loan. It pays from the check queue like
            any other and books on the company's default trust account rather than a loan's ledger.
          </p>
        )}
      </div>
    </Modal>
  )
}
